<template>
    <div v-if="visible" class="dev-panel">
        <div class="dev-panel-button">
            <Button
                :onClick="toggleModal"
                ariaLabel="open developer panel settings"
                icon="Cog"
                size="s"
                iconPosition="left"
                color="white" />
        </div>

        <Drawer
            v-if="modal"
            position="right"
            title="Dev Panel"
            size="l"
            :onClose="toggleModal">
            <div class="dev-panel-modal">
                <div>
                    <div class="dev-panel-dropdown">
                        <Select
                            v-model="formMarket"
                            :items="formMarkets"
                            label="Market"
                            placeholder="Choose market" />
                    </div>

                    <div class="dev-panel-section">
                        <Checkbox v-model="data.enabled" density="compact">
                            Use fake data
                        </Checkbox>

                        <TransitionExpand class="dev-panel-section">
                            <div v-if="data.enabled">
                                <AuthPanel
                                    v-if="page === DevPanelPage.Checkout"
                                    :auth="data.auth"
                                    :checkout="data.checkout"
                                    :page="page" />

                                <ChatWidgetPanel
                                    v-if="page === DevPanelPage.Booking"
                                    :chatWidget="data.chatWidget" />

                                <ProductsPanel
                                    v-if="
                                        page === DevPanelPage.Checkout ||
                                        page === DevPanelPage.Booking ||
                                        page === DevPanelPage.Payment
                                    "
                                    v-model:products="data.products"
                                    :page="page" />

                                <CheckoutPanel
                                    v-if="page === DevPanelPage.Checkout"
                                    :data="data.checkout" />

                                <PreCheckoutPanel
                                    v-if="page === DevPanelPage.PreCheckout"
                                    :data="data.preCheckout" />

                                <PaymentPanel
                                    v-if="page === DevPanelPage.Payment"
                                    :data="data.checkout" />
                                <BookingPanel
                                    v-if="page === DevPanelPage.Booking"
                                    :booking="data.booking"
                                    :session="data.bookingSession" />

                                <BookingsPanel
                                    v-if="page === DevPanelPage.Bookings"
                                    :data="data.bookings" />

                                <DebugPanel
                                    v-if="page === DevPanelPage.Checkout"
                                    :data="data"
                                    :page="page" />
                            </div>
                        </TransitionExpand>
                    </div>
                </div>

                <MessagesPanel :page="page" />
            </div>

            <template #footer>
                <div class="dev-panel-footer">
                    <ButtonText :onClick="onReset" size="l"> Reset & Refresh </ButtonText>

                    <ButtonPrimary :onClick="onSaveForm" size="l">
                        Save & Refresh
                    </ButtonPrimary>
                </div>
            </template>
        </Drawer>
    </div>
</template>

<script lang="ts">
    import { useStore } from '@/store';
    import { ref, defineComponent, computed, onErrorCaptured } from 'vue';
    import { configBase } from '@/config/base';
    import {
        isBookingPage,
        isBookingsPage,
        isCheckoutPage,
        isPaymentPage,
        isPreCheckoutPage,
    } from '@/helpers/url';
    import { setStoredMarketNumber } from '@shared/helpers/localStorage';
    import {
        getMarketsOptions,
        getStoredFakeData,
        setStoredFakeData,
    } from './DevPanel.helpers';
    import { DevPanelPage, FakeData } from './DevPanel.interfaces';
    import DebugPanel from './Panels/Debug.vue';
    import AuthPanel from './Panels/Auth.vue';
    import ChatWidgetPanel from './Panels/ChatWidget.vue';
    import ProductsPanel from './Panels/Products.vue';
    import CheckoutPanel from './Panels/Checkout.vue';
    import PreCheckoutPanel from './Panels/PreCheckout.vue';
    import PaymentPanel from './Panels/Payment.vue';
    import BookingPanel from './Panels/Booking.vue';
    import BookingsPanel from './Panels/Bookings.vue';
    import MessagesPanel from './Panels/Messages.vue';
    import Drawer from '@ui/components/Drawer/Drawer.vue';
    import TransitionExpand from '@ui/components/TransitionExpand/TransitionExpand.vue';
    import Button from '@ui/components/Button/Button.vue';
    import ButtonPrimary from '@ui/components/Button/ButtonPrimary.vue';
    import ButtonText from '@ui/components/Button/ButtonText.vue';
    import Checkbox from '@ui/components/FormV2/Fields/Checkbox/Checkbox.vue';
    import Select from '@ui/components/FormV2/Fields/Select/Select.vue';

    export default defineComponent({
        name: 'DevPanel',
        components: {
            DebugPanel,
            AuthPanel,
            ChatWidgetPanel,
            ProductsPanel,
            CheckoutPanel,
            PreCheckoutPanel,
            PaymentPanel,
            BookingPanel,
            BookingsPanel,
            MessagesPanel,
            Button,
            Select,
            ButtonText,
            ButtonPrimary,
            Drawer,
            TransitionExpand,
            Checkbox,
        },
        setup() {
            const store = useStore();
            const modal = ref<boolean>(false);

            const data = ref<FakeData>(getStoredFakeData());

            onErrorCaptured((error) => {
                console.error('Error Captured', error);
                setStoredFakeData(null);
                data.value = getStoredFakeData();
            });

            const page = computed(() => {
                if (isBookingPage()) {
                    return DevPanelPage.Booking;
                }

                if (isBookingsPage()) {
                    return DevPanelPage.Bookings;
                }

                if (isCheckoutPage()) {
                    return DevPanelPage.Checkout;
                }

                if (isPaymentPage()) {
                    return DevPanelPage.Payment;
                }

                if (isPreCheckoutPage()) {
                    return DevPanelPage.PreCheckout;
                }

                return DevPanelPage.Unknown;
            });

            const market = computed(() => store.state.general.market);
            const formMarkets = computed(() => getMarketsOptions());
            const formMarket = ref<number>(market.value?.number);

            const visible = computed(() => configBase.devMode.enabled);

            const toggleModal = () => {
                modal.value = !modal.value;
            };

            const onSaveForm = () => {
                setStoredMarketNumber(formMarket.value);
                setStoredFakeData(data.value);
                window.location.reload();
            };

            const onReset = () => {
                setStoredMarketNumber(null);
                setStoredFakeData(null);
                window.location.reload();
            };

            return {
                onSaveForm,
                onReset,
                data,
                page,
                formMarket,
                formMarkets,
                visible,
                modal,
                toggleModal,
                DevPanelPage,
            };
        },
    });
</script>

<style lang="scss">
    @forward './DevPanel.scss';
</style>
